import { Search, X, Home, Settings, Moon, Sun, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEffect, useRef, useState } from 'react';
import { usePrefs } from '@/store/prefs';
import { setTheme as applyTheme, type Theme } from '@/lib/theme';
import { SettingsDialog } from '@/components/layout/SettingsDialog';
import { cn } from '@/lib/cn';

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  isBrowsing: boolean;
  onHome: () => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
}

export function Header({
  query,
  onQueryChange,
  isBrowsing,
  onHome,
  searchInputRef,
}: Props) {
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const cycleTheme = () => {
    const order: Theme[] = ['dark', 'light', 'system'];
    const next = order[(order.indexOf(theme) + 1) % order.length]!;
    setTheme(next);
    applyTheme(next);
  };

  const ThemeIcon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;

  return (
    <header className="sticky top-0 z-20 flex items-center gap-1.5 border-b border-border bg-background/95 px-2 py-2 backdrop-blur">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onHome}
        title={isBrowsing ? 'Already at home' : 'Back to all tools (Esc)'}
        aria-label="Home"
      >
        <Home className="h-4 w-4" />
      </Button>
      <SearchBox
        query={query}
        onQueryChange={onQueryChange}
        inputRef={searchInputRef}
      />
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => setSettingsOpen(true)}
        title="Settings"
        aria-label="Settings"
      >
        <Settings className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={cycleTheme}
        title={`Theme: ${theme} (click to cycle)`}
      >
        <ThemeIcon className="h-3.5 w-3.5" />
      </Button>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </header>
  );
}

interface SearchProps {
  query: string;
  onQueryChange: (q: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

function SearchBox({ query, onQueryChange, inputRef }: SearchProps) {
  const localRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef) {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        localRef.current;
    }
  }, [inputRef]);
  return (
    <div className="relative flex-1">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={localRef}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Search developer tools..."
        className="pl-7 pr-7"
        aria-label="Search tools"
      />
      {query ? (
        <button
          type="button"
          onClick={() => onQueryChange('')}
          className={cn(
            'absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-secondary',
          )}
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
